/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, Children, useState, createRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import { ProgressIndicator, ProgressStep } from '@carbon/react';
import trackComponentEvent from '../../../Utilities/analytics/analytics.js';
import { noopFn, emptyArray, emptyObject } from '../../../Utilities/propHelpers/defaultProps.js';
import SidePanelContainer from './SidePanelContainer.js';
import SidePanelBreadcrumbs from './children/SidePanelBreadcrumbs.js';
import SidePanelCloseButton from './children/SidePanelCloseButton.js';
import SidePanelContent from './children/SidePanelContent.js';
import SidePanelControls from './children/SidePanelControls.js';
import SidePanelNestedPanels from './children/SidePanelNestedPanels.js';
import SidePanelMultiStep from './children/SidePanelMultiStep.js';
import { getPanelId } from './utils/getPanelDetails.js';
import callIf from './utils/callIf.js';
import getAllTabElements from './utils/getAllTabElements.js';
import SidePanelFocusTrap from './children/SidePanelFocusTrap.js';

var _excluded = ["breadCrumbText", "cancelText", "children", "className", "closePanelText", "cloneNavProps", "doneText", "hideBottomNav", "hasScrollContent", "id", "isMultiStep", "setStep", "getCurrentStepInfo", "nestedPanels", "nextText", "onBreadCrumbClick", "onCancelClick", "onCloseClick", "onCloseWithModalHandler", "onDoneClick", "onNextClick", "onPreviousClick", "previousText", "primaryButtonDisabled", "primaryButtonDanger", "secondaryButtonDisabled", "selectorPrimaryFocus", "title", "willClose", "doneIsLoading", "doneIsLoadingText", "analytics", "internal"];

/**
 * A custom hook to return the previous state.
 */
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
var SidePanel = function SidePanel(props) {
  var breadCrumbText = props.breadCrumbText,
    cancelText = props.cancelText,
    children = props.children,
    className = props.className,
    closePanelText = props.closePanelText,
    _props$cloneNavProps = props.cloneNavProps,
    cloneNavProps = _props$cloneNavProps === void 0 ? false : _props$cloneNavProps,
    doneText = props.doneText,
    hideBottomNav = props.hideBottomNav,
    hasScrollContent = props.hasScrollContent,
    id = props.id,
    isMultiStep = props.isMultiStep,
    setStep = props.setStep,
    _props$getCurrentStep = props.getCurrentStepInfo,
    getCurrentStepInfo = _props$getCurrentStep === void 0 ? noopFn : _props$getCurrentStep,
    _props$nestedPanels = props.nestedPanels,
    nestedPanels = _props$nestedPanels === void 0 ? emptyArray : _props$nestedPanels,
    nextText = props.nextText,
    onBreadCrumbClick = props.onBreadCrumbClick,
    onCancelClick = props.onCancelClick,
    onCloseClick = props.onCloseClick,
    onCloseWithModalHandler = props.onCloseWithModalHandler,
    onDoneClick = props.onDoneClick,
    onNextClick = props.onNextClick,
    onPreviousClick = props.onPreviousClick,
    previousText = props.previousText,
    primaryButtonDisabled = props.primaryButtonDisabled,
    primaryButtonDanger = props.primaryButtonDanger,
    secondaryButtonDisabled = props.secondaryButtonDisabled,
    selectorPrimaryFocus = props.selectorPrimaryFocus,
    title = props.title,
    _props$willClose = props.willClose,
    willClose = _props$willClose === void 0 ? false : _props$willClose,
    _props$doneIsLoading = props.doneIsLoading,
    doneIsLoading = _props$doneIsLoading === void 0 ? false : _props$doneIsLoading,
    doneIsLoadingText = props.doneIsLoadingText,
    analytics = props.analytics,
    _props$internal = props.internal,
    _props$internal2 = _props$internal === void 0 ? emptyObject : _props$internal,
    panelSize = _props$internal2.panelSize,
    containerPanelProps = _props$internal2.containerPanelProps,
    setActivePanelById = _props$internal2.setActivePanelById,
    activePanelId = _props$internal2.activePanelId,
    closePanel = _props$internal2.closePanel,
    childCloseRef = _props$internal2.childCloseRef,
    nextId = _props$internal2.nextId,
    previousId = _props$internal2.previousId,
    renderPrimary = _props$internal2.renderPrimary,
    breadcrumbs = _props$internal2.breadcrumbs,
    isOpen = _props$internal2.isOpen,
    isActive = _props$internal2.isActive,
    hasOverlay = _props$internal2.hasOverlay,
    modalOnDismiss = _props$internal2.modalOnDismiss,
    rest = _objectWithoutProperties(props, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var thisPanel = useRef();
  // MultiStep logic
  var steps = Children.toArray(children);
  // If MiniOrderSummary and MultiStep
  var miniOrderContent;
  var miniOrderSummary;
  var hasMiniOrderSummary = isMultiStep ? steps[steps.length - 1].props.className === 'cpc--mini-order-summary-side-panel__summary' : null;
  if (hasMiniOrderSummary && isMultiStep) {
    // eslint-disable-next-line prefer-destructuring
    var _steps = steps;
    var _steps2 = _slicedToArray(_steps, 2);
    miniOrderContent = _steps2[0];
    miniOrderSummary = _steps2[1];
    steps = miniOrderContent.props.children;
  }
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentStep = _useState2[0],
    setCurrentStep = _useState2[1];
  var stepRefs = useRef(isMultiStep ? steps.map(function () {
    return /*#__PURE__*/createRef();
  }) : null);
  var headingId = "".concat(id, "-heading");
  var prevNestedLength = usePrevious(nestedPanels.length);
  var prevIsActive = usePrevious(isActive);
  var prevIsOpen = usePrevious(isOpen);
  var focusOnLast = function focusOnLast() {
    var tabElements = getAllTabElements(thisPanel.current);
    if (tabElements.length > 0) {
      tabElements[tabElements.length - 1].focus();
    }
  };
  var focusOnFirst = function focusOnFirst() {
    var tabElements = getAllTabElements(thisPanel.current);
    if (tabElements.length > 0) {
      tabElements[0].focus();
    }
  };
  var onTabToLast = function onTabToLast(event) {
    if (hasOverlay && event.key === 'Tab' && event.shiftKey && thisPanel.current) {
      event.preventDefault();
      focusOnLast();
    }
  };

  // Focus on either the first breadcrumb or the close button.
  var onTabToFirst = function onTabToFirst(event) {
    if (hasOverlay && event.key === 'Tab' && !event.shiftKey && thisPanel.current) {
      event.preventDefault();
      focusOnFirst();
    }
  };
  var trackEvent = useCallback(function (action) {
    if (analytics) {
      trackComponentEvent('User Form', {
        action: action || 'SidePanel action',
        title: typeof title === 'string' ? title : '',
        // TODO: include a prop for analytics title for node titles?
        palComponent: 'SidePanel',
        category: 'Offering Interface',
        data: id
      });
    }
  }, [title, id, analytics]);

  // Focus on the pages first element or the previous / done button.
  useEffect(function () {
    if (id === activePanelId && isOpen) {
      // Set a timeout here to allow the side panels visibility to be set
      setTimeout(function () {
        if (thisPanel.current) {
          var firstFocus = thisPanel.current.querySelector(selectorPrimaryFocus || '[data-focus-first]');
          var tabElements = getAllTabElements(thisPanel.current.querySelector('.cpc--side-panel__body-content'));
          // If specified focus on the first focusable element.
          if (firstFocus) {
            firstFocus.focus();
            return;
          }
          // Focus on the first element in the content.
          if (tabElements.length > 0) {
            tabElements[0].focus();
            return;
          }
          // If we're not hiding the bottom nav, focus on the last button.
          if (!hideBottomNav) {
            focusOnLast();
            return;
          }
          // Otherwise focus on the first focusable element.
          focusOnFirst();
        }
      }, 100);
    }
  }, [activePanelId, hideBottomNav, id, isOpen, selectorPrimaryFocus]);

  // TODO: Deprecate this way of working with the SidePanel in the future.
  useEffect(function () {
    var currentLength = nestedPanels.length;
    var lastIndex = currentLength - 1;
    if (prevNestedLength < currentLength) {
      setActivePanelById(getPanelId(nestedPanels[lastIndex], lastIndex));
    }
    if (nestedPanels.length > 0) {
      nestedPanels.reduce(function (hasSetActive, panel, index, panels) {
        if (hasSetActive) {
          return hasSetActive;
        }
        if (panel.props.willClose) {
          var previousPanel = panels[index - 1];
          var previousNestedOrParentId = previousPanel ? getPanelId(previousPanel, index) : id;
          setActivePanelById(previousNestedOrParentId);
          return true;
        }
        return false;
      }, false);
    }
  }, [id, nestedPanels, nextId, prevNestedLength, previousId, setActivePanelById, willClose]);

  // Set the child's close ref to the onCloseClick function.
  useEffect(function () {
    if (id === activePanelId) {
      // eslint-disable-next-line no-param-reassign
      childCloseRef.current = modalOnDismiss ? function () {
        return onCloseWithModalHandler(onCloseClick);
      } : onCloseClick;
    }
  }, [onCloseClick, id, activePanelId, childCloseRef, onCloseWithModalHandler, modalOnDismiss]);
  useEffect(function () {
    // because prevIsActive is tracked here, this is going to fire 2x expected, but the logic already handles that
    if (isActive && isOpen && (!prevIsActive || !prevIsOpen)) trackEvent('SidePanel Open');else if (!isActive && prevIsActive) trackEvent('SidePanel Close ');
  }, [title, isActive, prevIsActive, prevIsOpen, isOpen, trackEvent]);

  // Multi Step logic
  var stepsLabels = [];
  var onDoneMultiStep = function onDoneMultiStep() {
    return setCurrentStep(0);
  };
  var onCloseMultiStep = function onCloseMultiStep() {
    return setCurrentStep(0);
  };
  var onNextStep = function onNextStep() {
    return setCurrentStep(currentStep + 1);
  };
  var onPreviousStep = function onPreviousStep() {
    return setCurrentStep(currentStep - 1);
  };
  if (steps && isMultiStep) {
    steps.forEach(function (step) {
      stepsLabels.push({
        label: step.props.title || step.props.id,
        id: step.props.id
      });
    });
  }
  var nextIdMultiStep;
  var previousIdMultiStep;
  if (isMultiStep && stepsLabels[currentStep + 1]) {
    nextIdMultiStep = stepsLabels[currentStep + 1].id;
  }
  if (isMultiStep) {
    if (stepsLabels[currentStep - 1]) {
      previousIdMultiStep = stepsLabels[currentStep - 1].id;
    } else {
      previousIdMultiStep = null;
    }
  }
  // Get information about steps
  useEffect(function () {
    if (isMultiStep && getCurrentStepInfo) {
      getCurrentStepInfo({
        currentStep: currentStep,
        currentStepId: steps[currentStep].props.id,
        currentStepLabel: steps[currentStep].props.title
      });
    }
  }, [currentStep, getCurrentStepInfo, isMultiStep, steps]);
  // Programmatically set step
  if (setStep) {
    setStep(function (index) {
      if (stepsLabels[index]) {
        setCurrentStep(index);
      }
    });
  }
  var multiStep = isMultiStep ? steps.map(function (step, index) {
    return /*#__PURE__*/React.createElement(SidePanelMultiStep, {
      key: "index-".concat(step.props.id),
      ref: stepRefs.current[index],
      id: "".concat(step.props.id, "-multi-step"),
      "aria-hidden": currentStep === index ? 'false' : 'true',
      classNames: classnames('cpc--side-panel__multi-step-content', {
        'cpc--side-panel__multi-step-content--open': step.props.id === stepsLabels[currentStep].id,
        'cpc--side-panel__multi-step-content--next': step.props.id === nextIdMultiStep,
        'cpc--side-panel__multi-step-content--previous': step.props.id === previousIdMultiStep,
        'cpc--side-panel__multi-step-content--hidden-in-place': step.props.id !== stepsLabels[currentStep].id && step.props.id !== nextIdMultiStep && step.props.id !== previousIdMultiStep
      }),
      setActivePanelById: setActivePanelById
    }, step);
  }) : null;
  var miniOrderProps = {};
  var miniOrderSummaryMultiStep = /*#__PURE__*/React.isValidElement(miniOrderContent) ? /*#__PURE__*/React.cloneElement(miniOrderContent, miniOrderProps, multiStep) : null;
  // A function to call when closing the side panel
  var onClose = callIf(onCloseClick, isMultiStep ? onCloseMultiStep : closePanel);
  // A function to call when the next button is clicked.
  var onNext = callIf(onNextClick, isMultiStep ? onNextStep : function () {
    return setActivePanelById(nextId);
  });
  // A function to call when the done button is clicked.
  var onDone = function onDone() {
    trackEvent("Sidepanel Done");
    callIf(onDoneClick, isMultiStep ? onDoneMultiStep : closePanel)();
  };
  // A function to call when the cancel button is clicked.
  var onCancel = function onCancel() {
    trackEvent('Sidepanel Cancel clicked');
    callIf(onCancelClick, closePanel)();
  };
  // A function to call when the previous button is clicked.
  var onPrevious = callIf(onPreviousClick, isMultiStep ? onPreviousStep : function () {
    return setActivePanelById(previousId);
  });
  return /*#__PURE__*/React.createElement(SidePanelFocusTrap, {
    internal: {
      isOpen: isOpen,
      labeledBy: headingId
    }
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: thisPanel,
    key: id,
    id: id,
    className: classnames('cpc--side-panel', {
      'cpc--side-panel--open': isOpen,
      'cpc--side-panel--small': panelSize === 'small',
      'cpc--side-panel--xl': panelSize === 'xl',
      'cpc--side-panel--has-bottom-nav': !hideBottomNav
    }, className),
    "data-is-active": id === activePanelId
  }, rest), breadcrumbs && /*#__PURE__*/React.createElement(SidePanelBreadcrumbs, {
    activePanelId: activePanelId,
    breadcrumbs: breadcrumbs,
    hasOverlay: hasOverlay,
    onBreadCrumbClick: onBreadCrumbClick,
    onPageSelect: setActivePanelById,
    onTabToPrev: onTabToLast
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames('cpc--side-panel__content', {
      'cpc--side-panel__content--scroll': hasScrollContent,
      'cpc--side-panel__multi-step__parent': isMultiStep
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--side-panel__heading--close-button"
  }, /*#__PURE__*/React.createElement("h3", {
    id: headingId,
    className: "cpc--side-panel__heading"
  }, title), /*#__PURE__*/React.createElement(SidePanelCloseButton, {
    activePanelId: activePanelId,
    focusOnLast: !breadcrumbs && hasOverlay,
    iconDescription: closePanelText || t('Close Panel'),
    onClose: onClose,
    panelId: id,
    onTabToPrev: onTabToLast,
    onTabToNext: function onTabToNext(event) {
      if (getAllTabElements(thisPanel.current).length === 1) {
        onTabToFirst(event);
      }
    }
  })), isMultiStep && /*#__PURE__*/React.createElement(ProgressIndicator, {
    spaceEqually: true,
    currentIndex: currentStep,
    className: "cpc--side-panel__progress-indicator"
  }, stepsLabels.map(function (step) {
    return /*#__PURE__*/React.createElement(ProgressStep, {
      label: step.label,
      key: "key-".concat(step.id)
    });
  })), /*#__PURE__*/React.createElement(SidePanelContent, {
    activePanelId: activePanelId,
    cloneNavProps: cloneNavProps,
    nextId: isMultiStep ? nextIdMultiStep : nextId,
    previousId: isMultiStep ? previousIdMultiStep : previousId,
    setActivePanelById: setActivePanelById
  }, isMultiStep && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    },
    className: hasMiniOrderSummary && miniOrderContent.props ? miniOrderContent.props.className : ''
  }, hasMiniOrderSummary ? miniOrderSummaryMultiStep : multiStep), hasMiniOrderSummary && miniOrderSummary) || children)), !hideBottomNav && /*#__PURE__*/React.createElement(SidePanelControls, {
    cancelText: cancelText || t('Cancel'),
    doneText: doneText || t('Done'),
    hasOverlay: hasOverlay,
    nextId: isMultiStep ? nextIdMultiStep : nextId,
    nextText: nextText || t('Next'),
    onCancel: onCancel,
    onNext: onNext,
    onDone: onDone,
    onPrevious: onPrevious,
    previousText: previousText || t('Previous'),
    primaryButtonDisabled: primaryButtonDisabled || id !== activePanelId,
    primaryButtonDanger: primaryButtonDanger,
    renderPrimaryButton: renderPrimary,
    secondaryButtonDisabled: secondaryButtonDisabled || id !== activePanelId,
    previousId: isMultiStep ? previousIdMultiStep : previousId,
    doneIsLoading: doneIsLoading,
    doneIsLoadingText: doneIsLoadingText || t('Loading...')
  })), /*#__PURE__*/React.createElement(SidePanelNestedPanels, {
    activePanelId: activePanelId,
    isOpen: isOpen,
    parentBreadcrumbText: isMultiStep ? stepsLabels[currentStep].label : breadCrumbText,
    panelSize: panelSize,
    closePanel: closePanel,
    childCloseRef: childCloseRef,
    containerPanelProps: containerPanelProps,
    nestedPanels: nestedPanels,
    onPageSelect: setActivePanelById,
    hasOverlay: hasOverlay,
    setActivePanelById: setActivePanelById,
    parentPanelId: id
  }));
};
// Set a display name so we can easily filter for these.
SidePanel.displayName = 'SidePanel';
SidePanel.SidePanelContainer = SidePanelContainer;
SidePanel.propTypes = {
  /**
   * A unique identifier for a sidePanel component. This is used for pagination purposes and can be useful for setting
   * which SidePanel to currently be viewing.
   */
  id: PropTypes.string.isRequired,
  /**
   * The breadcrumb text for this panel. It should be assigned if this is or has a nested panel.
   */
  breadCrumbText: PropTypes.string,
  /**
   * The text for the "cancel" button, used when on the first panel. If not provided, this will be inherited
   * from the parent sidePanelContainer.
   */
  cancelText: PropTypes.string,
  /**
   * A custom class name to provide to the side panel component.
   */
  className: PropTypes.string,
  /**
   * Whether navigational props should be cloned onto the side panels children. This provides props like
   * setActivePanelById, previousPanelId, nextPanelId, and activePanelId to the Side Nav's child.
   */
  cloneNavProps: PropTypes.bool,
  /**
   * The text for the close icon's tooltip in the right hand corner of the side panel.
   */
  closePanelText: PropTypes.string,
  /**
   * The text for the "done" button, used when on the last panel or a panel in a set of nested panels.
   * If not provided, this will be inherited from the parent sidePanelContainer.
   */
  doneText: PropTypes.string,
  /**
   * If the SidePanel has scroll content. If not provided, then its default is false.
   */
  hasScrollContent: PropTypes.bool,
  /**
   * The text for the "next" button for side panels. If not provided, this will be inherited
   * from the parent sidePanelContainer.
   */
  nextText: PropTypes.string,
  /**
   * The text for the "previous" button for panels after the first panel of a set. If not provided, this will be inherited
   * from the parent sidePanelContainer.
   */
  previousText: PropTypes.string,
  /**
   * Prop from container that determines all sidepanels will be Multi Step.
   */
  isMultiStep: PropTypes.bool,
  /**
   * A function to be called to return the setStep function. The setStep function will accept step number
   * iterating ([0, number of steps-1 ]) to switch to the desired Step
   */
  setStep: PropTypes.func,
  /**
   * A function to be called to return the current step data. The function is called on every step change
   * returning an object with label, id and current-step index.
   */
  getCurrentStepInfo: PropTypes.func,
  /**
   * The title of a side panel. Only needed if the panel is or will have a nested panel, or if
   * the title of one of a sequence of panels is different from its SidePanelContainer. Elements or strings can be rendered inside of the title prop.
   */
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /**
   * Custom function that will be called when the "cancel" button is clicked. If the function does not return a truthy value,
   * the default action of the "cancel" button will be cancelled. The function may also return a promise whose value is resolved later.
   * If a function is not provided here, an equivalent function assigned in the parent SidePanelContainer will be inherited.
   */
  onCancelClick: PropTypes.func,
  /**
   * Function that will be called when a "breadcrumb"  is clicked. If the function does not return a truthy value,
   * the default action of the breadcrumb will be cancelled. The function may also return a promise whose value is resolved later.
   * If a function is not provided here, an equivalent function assigned in the parent SidePanelContainer will be inherited.
   */
  onBreadCrumbClick: PropTypes.func,
  /**
   * Custom function that will be called when the "close" button (x) is clicked. The return value is disregarded.
   * The function may also return a promise whose value is resolved later. If a function is not provided here,
   * an equivalent function assigned in the parent SidePanelContainer will be inherited.
   */
  onCloseClick: PropTypes.func,
  /**
   * Custom function that will be called when the "done" button is clicked. If the function does not return a truthy value,
   * the default action of the "done" button will be cancelled. When in a nested panel this will take the panel to the previous page.
   * When in a side panel that is not nested this will trigger the panel to close. The function may also return a promise whose value is resolved later.
   * If a function is not provided here, an equivalent function assigned in the parent SidePanelContainer will be inherited, except
   * in the case of a nested side panel
   */
  onDoneClick: PropTypes.func,
  /**
   * Custom function that will be called when the "next" button is clicked. If the function does not return a truthy value,
   * the default action of the "next" button will be cancelled. The function may also return a promise whose value is resolved later.
   * If a function is not provided here, an equivalent function assigned in the parent SidePanelContainer will be inherited.
   */
  onNextClick: PropTypes.func,
  /**
   * Custom function that will be called when the "previous" button is clicked. If the
   * function does not return a truthy value, the default action of the "previous" button
   * will be cancelled. The function may also return a promise whose value is resolved later.
   * If a function is not provided here, an equivalent function assigned in the parent SidePanelContainer will be inherited.
   */
  onPreviousClick: PropTypes.func,
  /**
   * Specify a CSS selector that matches the DOM element that should be focused on when SidePanel opens
   */
  selectorPrimaryFocus: PropTypes.string,
  /**
   * Whether or not the primary button should be disabled.
   */
  primaryButtonDisabled: PropTypes.bool,
  /**
   * Whether or not the primary button should be danger type
   */
  primaryButtonDanger: PropTypes.bool,
  /**
   * Whether or not the secondary button should be disabled.
   */
  secondaryButtonDisabled: PropTypes.bool,
  /**
   * The content of the SidePanel. If a component is passed in, a few internal props will be cloned onto the component
   * to help manage pagination. These are setActivePanelID,  previousId, activePanelId, and nextId.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /**
   * An array of nested panels that can be displayed if activated. To active call the setNestedPanel prop that
   * is cloned into the child's content.
   */
  nestedPanels: PropTypes.arrayOf(PropTypes.node),
  /**
   * If this property is true, the bottom nav will not be rendered.
   */
  hideBottomNav: PropTypes.bool,
  /**
   * If this property is true, the this sidePanel will slide closed to prepare to be removed. If you are removing
   * a nested panel from the array of nestedPanels this should be removed 400ms before removal. If you are using
   * the setActivePanelById render prop this can be ignored.
   */
  willClose: PropTypes.bool,
  /**
   * If this property is true, the primary button will be replaced with a loading state until the prop is set back to false.
   */
  doneIsLoading: PropTypes.bool,
  /**
   * This optional prop allows customization of the text shown while the primary button is in a loading state.
   */
  doneIsLoadingText: PropTypes.string,
  /**
   * This optional prop allows the component to be tracked. By default the boolean is false and the component will not be tracked.
   * If you want to track this component's usage set the boolean to be true.
   */
  analytics: PropTypes.bool,
  /**
   * Internal props for managing state within the component. These should not be set outside of the component
   * and will be ignored if attempted to be set.
   */
  internal: PropTypes.shape({
    panelSize: PropTypes.string,
    containerPanelProps: PropTypes.shape({}),
    setActivePanelById: PropTypes.func,
    activePanelId: PropTypes.string,
    nextId: PropTypes.string,
    previousId: PropTypes.string,
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      id: PropTypes.string
    })),
    isOpen: PropTypes.bool,
    isActive: PropTypes.bool,
    closePanel: PropTypes.func,
    setActivePanel: PropTypes.func,
    hasOverlay: PropTypes.bool,
    renderPrimary: PropTypes.bool,
    childCloseRef: PropTypes.shape({
      current: PropTypes.func
    }),
    modalOnDismiss: PropTypes.bool
  }),
  /**
   * Gets fired if passed when clicking outside the sidepanel
   */
  onCloseWithModalHandler: PropTypes.func
};
var SidePanel$1 = SidePanel;

export { SidePanelContainer, SidePanel$1 as default };
